import type {
  SDKRequest, SDKRequestDeleteOptions, SDKRequestGetOptions, SDKRequestOptions, SDKRequestPostOptions,
  SDKRequestPutOptions, SDKResponseOptions,
} from "@nebula-sdk/core";

// 通用请求
const request = async <T>(options: SDKRequestOptions): Promise<SDKResponseOptions<T>> => {
  return await new Promise<SDKResponseOptions<T>>((resolve) => {
    const origins: Record<string, string> = {
      "https://dev-pay.feiyuapi.com": "https://dev-api.feiyuapi.com",
      "https://sit-pay.feiyuapi.com": "https://sit-api.feiyuapi.com",
      "https://pay.feiyuapi.com": "https://api.feiyuapi.com",
    };

    for (const key in origins) {
      if (!window.location.origin.includes(key)) continue;
      const source = import.meta.env.VITE_PASSPORT_API;
      options.url = options.url.replace(source, origins[key]);
    }

    const xhr = new XMLHttpRequest();

    xhr.timeout = options.timeout ?? 60000;

    if (["GET", "DELETE"].includes(options.method)) {
      if (options.data) {
        const query = [];
        for (const key in options.data) {
          const value = options.data[key];
          if (value === null || value === undefined) continue;
          query.push(`${key}=${encodeURIComponent(value as string)}`);
        }
        xhr.open(options.method, `${options.url}?${query.join("&")}`);
      } else {
        xhr.open(options.method, options.url);
      }
    } else {
      xhr.open(options.method, options.url);
    }

    xhr.setRequestHeader("content-type", "application/json");
    xhr.setRequestHeader("x-host", window.location.host);
    if (options.headers) {
      for (const key in options.headers) {
        xhr.setRequestHeader(key, options.headers[key] as string);
      }
    }

    xhr.onload = () => {
      const data = JSON.parse(xhr.responseText || "{}");

      if (xhr.status !== 200) {
        resolve({ status: "error", code: -1, msg: "error", data: data as T });
        return;
      }

      const internalUrlList: Array<string> = [
        import.meta.env.VITE_GATEWAY_API,
        import.meta.env.VITE_PASSPORT_API,
        import.meta.env.VITE_COMBINE_API,
        import.meta.env.VITE_SERVICE_API,
      ];

      for (const key in origins) {
        if (!window.location.origin.includes(key)) continue;
        internalUrlList.push(origins[key]);
      }

      if (internalUrlList.every((url) => !options.url.includes(url))) {
        resolve({ status: "success", code: 0, msg: "success", data: data as T });
        return;
      }
      resolve({
        key: data.request_id,
        status: data.status === 0 ? "success" : "error",
        code: data.status as number,
        msg: data.message as string,
        data: data.data as T,
      });
    };

    xhr.onerror = () => {
      resolve({ key: -1, status: "error", code: -1, msg: "network error", data: null as T });
    };

    xhr.ontimeout = () => {
      resolve({ key: -1, status: "error", code: -1, msg: "request timeout", data: null as T });
    };

    xhr.send(options.data ? JSON.stringify(options.data) : null);
  });
};

// 网络请求
const httpRequest: SDKRequest = {
  get: async <T>(options: SDKRequestGetOptions) => {
    return request<T>({ ...options, method: "GET" });
  },
  post: async <T>(options: SDKRequestPostOptions) => {
    return request<T>({ ...options, method: "POST" });
  },
  put: async <T>(options: SDKRequestPutOptions) => {
    return request<T>({ ...options, method: "PUT" });
  },
  delete: async <T>(options: SDKRequestDeleteOptions) => {
    return request<T>({ ...options, method: "DELETE" });
  },
};

export default httpRequest;
